import { APP_WARNING_NOTICE, BRAND_WORDS, BUTTON_LABELS } from '@/constants';
import { AuthLinks } from '@/components/Auth';
import { Button } from '@/components/Buttons';
import { cn } from '@/utils/cn';
import { FieldOneLogoSvg } from '@/components/Icons';
import { OutageAlertBanner } from '@/components/Alerts';
import React, { useState } from 'react';

export interface AuthLoginProps {
  /** Called when the Sign In button is clicked */
  onSignIn: () => void;
  /** Whether to show the outage alert banner */
  showOutageAlert?: boolean;
}

/**
 * AuthLogin Component
 *
 * Renders the login card with the FieldOne wordmark, sign-in button,
 * auth links, and legal warning notice.
 */
export const AuthLogin: React.FC<AuthLoginProps> = ({ onSignIn, showOutageAlert }) => {
  const [isPending, setIsPending] = useState(false);

  /**
   * Handles the Sign In button click by starting a transition and calling the
   * onSignIn prop. This allows for any state updates triggered by onSignIn to
   * be deferred until after the transition, improving perceived performance.
   */
  const handleSignIn = () => {
    setIsPending(true);
    onSignIn();
  };

  // ==============================
  // Sub-components
  // ==============================
  const LogoSection = () => (
    <div className="flex w-full items-center justify-center">
      <FieldOneLogoSvg />
    </div>
  );

  const HeaderSection = () => (
    <div className="text-center">
      <h1 className="typography-page-title mb-2 text-semantic-text-primary">
        {BRAND_WORDS.FIELDONE_ROLEPLAY_CERT_PLATFORM}
      </h1>
      <p className="typography-body-large text-semantic-text-secondary">
        Sign in to access your assigned modules
      </p>
    </div>
  );

  const ButtonSection = ({ enabled, loading }: { enabled: boolean; loading: boolean }) => (
    <div className="flex items-center justify-center p-4">
      <Button
        disabled={!enabled}
        isTransitioning={loading}
        label={BUTTON_LABELS.SIGN_IN}
        onClick={handleSignIn}
        variant="primary"
      />
    </div>
  );

  const AuthLinksSection = () => (
    <div className="p-4 text-center">
      <AuthLinks />
    </div>
  );

  const AppWarningSection = () => (
    <div className="p-0 text-center text-xs text-semantic-text-secondary">
      <p>{APP_WARNING_NOTICE}</p>
    </div>
  );

  // ==============================
  // Main Render
  // ==============================
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-6',
        'mt-12 md:mt-16 md:gap-8 lg:mt-20 lg:gap-10',
        {
          'mb-8 lg:mb-12': showOutageAlert,
        },
      )}
    >
      <LogoSection />
      <div className="flex w-full max-w-[575px] flex-col gap-2.5">
        {showOutageAlert && <OutageAlertBanner />}
        <div className="w-full rounded-lg bg-neutral-white p-8">
          <HeaderSection />
          <ButtonSection enabled={!showOutageAlert} loading={isPending} />
          <AuthLinksSection />
          <AppWarningSection />
        </div>
      </div>
    </div>
  );
};

AuthLogin.displayName = 'AuthLogin';

export default AuthLogin;
